<script setup lang="ts">
import { ref, computed } from 'vue'

import { getAxios } from '../../api/axios'
import { isEmail } from '../../utils/strings'
import { useConfigStore } from '../../stores/config'

import type { ContactFormParagraph } from '../../utils/paragraph-schema/paragraphs'
import type { Brand } from '@xcel-next/core/enums'

const props = defineProps<{
  contactForm: ContactFormParagraph['contactForm'],
  brand?: Brand
}>()

const configStore = useConfigStore()

const errorMessage = ref('')
const confirmationMessage = ref('')
const name = ref('')
const actualEmail = ref('')
const subject = ref('')
const message = ref('')
const termsOfUse = ref(false)
const email = ref('')
const fname = ref('')
const lname = ref('')
const submitted = ref(false)

const buttonStyleClasses = computed(() => ({
  'bx-form__button': true,
  'bx-form__button--disabled': name.value === '' || actualEmail.value === '' || subject.value === '' || message.value === '' || !termsOfUse.value
}))

const handleSubmit = async () => {
  errorMessage.value = ''
  confirmationMessage.value = ''

  // if any of these are actually filled out, it will most likely be a bot submitting the form so we just ignore it.
  if (email.value || fname.value || lname.value) {
    return
  }

  if (!isEmail(actualEmail.value)) {
    errorMessage.value = 'Bitte eine gültige E-Mail-Adresse eingeben.'
    return
  }

  try {
    const axios = await getAxios()
    await axios.post((configStore.rsConfig).orchestrationServiceUrl + '/support/email', {
      brand: props.brand,
      actualEmail: actualEmail.value,
      name: name.value,
      subject: subject.value,
      message: message.value
    })

    submitted.value = true
    confirmationMessage.value = props.contactForm.confirmationMessage ?? ''
  } catch (e) {
    console.log(e)
  }
}
</script>

<template>
  <div class="bx-content bx-content__continuous-text bx-typo--content-continuous-text">
    <h2>{{ contactForm.title }}</h2>
    <form class="bx-form bx-typo--form bx-form--contact bx-typo--form-contact"
          @submit.prevent="handleSubmit">
      <div v-if="errorMessage && errorMessage.length > 0"
           class="bx-form__message bx-form__message--error">
        {{ errorMessage }}
      </div>
      <div v-if="confirmationMessage && confirmationMessage.length > 0">
        <svg class="icon icon-check">
          <use href="#check" />
        </svg>
        <span class="bx-form__message">{{ confirmationMessage }}</span>
      </div>
      <div v-if="!submitted">
        <label for="name"
               class="bx-form__label">
          Name
          <span class="bx-form__required"
                title="Diese Angabe wird benötigt.">*
          </span>
        </label>
        <input id="name"
               v-model="name"
               type="text"
               name="name"
               class="bx-form__input"
               title="Name">

        <label for="actualEmail"
               class="bx-form__label">
          E-Mail
          <span class="bx-form__required"
                title="Diese Angabe wird benötigt.">*
          </span>
        </label>
        <input id="actualEmail"
               v-model="actualEmail"
               type="email"
               name="actualEmail"
               class="bx-form__input"
               title="E-Mail-Adresse"
               autocomplete="off">

        <label for="subject"
               class="bx-form__label">
          Betreff
          <span class="bx-form__required"
                title="Diese Angabe wird benötigt.">*
          </span>
        </label>
        <div class="bx-form__select-wrapper">
          <select id="subject"
                  v-model="subject"
                  name="subject"
                  class="bx-form__select"
                  title="Bitte auswählen">
            <option value=""
                    disabled
                    selected>
              Bitte auswählen
            </option>
            <option value="Inhaltliche Rückmeldung">
              Inhaltliche Rückmeldung
            </option>
            <option value="Technische Rückmeldung">
              Technische Rückmeldung
            </option>
          </select>
          <svg class="icon icon-arrow_toggle">
            <use href="#arrow_toggle" />
          </svg>
        </div>

        <label for="message"
               class="bx-form__label">
          Nachricht
          <span class="bx-form__required"
                title="Diese Angabe wird benötigt.">*
          </span>
        </label>
        <textarea id="message"
                  v-model="message"
                  name="message"
                  class="bx-form__textarea"
                  title="Nachricht"
                  rows="5" />

        <div class="bx-form__checkbox-wrapper">
          <input id="checkbox"
                 v-model="termsOfUse"
                 type="checkbox"
                 name="checkbox"
                 class="bx-form__checkbox"
                 title="Bitte bestätigen">
          <label for="checkbox"
                 class="bx-form__label-checkbox"
                 v-html="contactForm.termsAndConditions" />
        </div>

        <p v-if="contactForm.infoText && contactForm.infoText.length > 0"
           class="bx-form__disclaimer">
          {{ contactForm.infoText }}
        </p>
        <button :disabled="!name || !actualEmail || !termsOfUse || !message"
                :class="buttonStyleClasses">
          Nachricht senden
        </button>
        <div class="bx-form__disclaimer-required">
          * Pflichtfelder
        </div>
      </div>

      <!-- Honeypot form elements to avoid submissions from bots -->
      <span class="bx-form__hp-wrapper">
        <input v-model="email"
               type="email"
               name="email"
               autocomplete="off">
        <input v-model="fname"
               type="text"
               name="fname"
               autocomplete="off">
        <input v-model="lname"
               type="text"
               name="lname"
               autocomplete="off">
      </span>
    </form>
  </div>
</template>
